import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Heart, Mail, Lock, Eye, EyeOff, User, HandHeart, HelpCircle, Building2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Register = () => {
  const { register, loginWithGoogle, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState('donor');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    vat_number: '',
    company_address: '',
    company_city: '',
    company_cap: '',
    company_region: ''
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/feed', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      toast.error(language === 'it' ? 'Devi accettare la privacy policy' : 'You must accept the privacy policy');
      return;
    }
    setLoading(true);

    try {
      const registrationData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        user_type: userType
      };

      if (userType === 'company') {
        registrationData.company_name = formData.company_name;
        registrationData.vat_number = formData.vat_number;
        registrationData.company_address = formData.company_address;
        registrationData.company_city = formData.company_city;
        registrationData.company_cap = formData.company_cap;
        registrationData.company_region = formData.company_region;
      }

      await register(registrationData);
      toast.success(language === 'it' ? 'Account creato con successo!' : 'Account created successfully!');
      navigate('/feed', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1741615331533-2360157c55aa?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Solidarity"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-emerald-900/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <Heart className="h-16 w-16 text-white mx-auto mb-6" fill="currentColor" />
            <h1 className="font-heading text-4xl font-bold text-white mb-4">
              {language === 'it' ? 'Unisciti a Moneyfor' : 'Join Moneyfor'}
            </h1>
            <p className="text-emerald-100 text-lg max-w-md">
              {language === 'it' 
                ? 'Che tu voglia donare, ricevere aiuto o rappresentare un\'azienda, sei nel posto giusto.'
                : 'Whether you want to donate, receive help, or represent a company, you\'re in the right place.'}
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-6">
            <Link to="/" className="flex items-center gap-2">
              <Heart className="h-8 w-8 text-emerald-800" fill="currentColor" />
              <span className="font-heading text-2xl font-bold text-stone-900">Moneyfor</span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 max-h-[85vh] overflow-y-auto">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
              {language === 'it' ? 'Registrati' : 'Register'}
            </h2>
            <p className="text-stone-600 mb-5 text-sm">
              {language === 'it' ? 'Crea il tuo account per iniziare' : 'Create your account to get started'}
            </p>

            {/* User Type Selection */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              <button
                type="button"
                data-testid="donor-type-btn"
                onClick={() => setUserType('donor')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === 'donor'
                    ? 'border-emerald-800 bg-emerald-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <HandHeart className={`h-5 w-5 mx-auto mb-1 ${userType === 'donor' ? 'text-emerald-800' : 'text-stone-400'}`} />
                <span className={`text-xs font-medium block ${userType === 'donor' ? 'text-emerald-800' : 'text-stone-600'}`}>
                  {language === 'it' ? 'Donatore' : 'Donor'}
                </span>
              </button>
              <button
                type="button"
                data-testid="recipient-type-btn"
                onClick={() => setUserType('recipient')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === 'recipient'
                    ? 'border-emerald-800 bg-emerald-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <HelpCircle className={`h-5 w-5 mx-auto mb-1 ${userType === 'recipient' ? 'text-emerald-800' : 'text-stone-400'}`} />
                <span className={`text-xs font-medium block ${userType === 'recipient' ? 'text-emerald-800' : 'text-stone-600'}`}>
                  {language === 'it' ? 'Richiedente' : 'Recipient'}
                </span>
              </button>
              <button
                type="button"
                data-testid="company-type-btn"
                onClick={() => setUserType('company')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  userType === 'company'
                    ? 'border-emerald-800 bg-emerald-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <Building2 className={`h-5 w-5 mx-auto mb-1 ${userType === 'company' ? 'text-emerald-800' : 'text-stone-400'}`} />
                <span className={`text-xs font-medium block ${userType === 'company' ? 'text-emerald-800' : 'text-stone-600'}`}>
                  {language === 'it' ? 'Azienda' : 'Company'}
                </span>
              </button>
            </div>

            {/* Google Login */}
            <Button
              data-testid="google-register-btn"
              type="button"
              variant="outline"
              className="w-full h-11 rounded-xl border-stone-200 hover:bg-stone-50 mb-4"
              onClick={loginWithGoogle}
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </Button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-stone-500">{language === 'it' ? 'oppure' : 'or'}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="name" className="text-sm">{language === 'it' ? 'Nome' : 'Name'}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    placeholder={userType === 'company' ? (language === 'it' ? 'Nome referente' : 'Contact name') : 'Mario Rossi'}
                    className="pl-10 h-11 rounded-xl border-stone-200 bg-stone-50 focus:bg-white text-sm"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Company-specific fields */}
              {userType === 'company' && (
                <div className="space-y-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <h3 className="font-semibold text-emerald-900 text-sm flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {language === 'it' ? 'Dati Azienda' : 'Company Details'}
                  </h3>
                  
                  <div className="space-y-1">
                    <Label className="text-sm">{language === 'it' ? 'Ragione Sociale' : 'Company Name'}</Label>
                    <Input
                      data-testid="company-name-input"
                      placeholder="Acme S.r.l."
                      className="h-10 rounded-lg border-stone-200 bg-white text-sm"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-sm">{language === 'it' ? 'Partita IVA' : 'VAT Number'}</Label>
                    <Input
                      data-testid="vat-number-input"
                      placeholder="IT12345678901"
                      className="h-10 rounded-lg border-stone-200 bg-white text-sm"
                      value={formData.vat_number}
                      onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-sm">{language === 'it' ? 'Indirizzo' : 'Address'}</Label>
                    <Input
                      data-testid="company-address-input"
                      placeholder="Via Roma 123"
                      className="h-10 rounded-lg border-stone-200 bg-white text-sm"
                      value={formData.company_address}
                      onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-sm">{language === 'it' ? 'Città' : 'City'}</Label>
                      <Input
                        data-testid="company-city-input"
                        placeholder="Milano"
                        className="h-10 rounded-lg border-stone-200 bg-white text-sm"
                        value={formData.company_city}
                        onChange={(e) => setFormData({ ...formData, company_city: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">CAP</Label>
                      <Input
                        data-testid="company-cap-input"
                        placeholder="20100"
                        className="h-10 rounded-lg border-stone-200 bg-white text-sm"
                        value={formData.company_cap}
                        onChange={(e) => setFormData({ ...formData, company_cap: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-sm">{language === 'it' ? 'Regione' : 'Region'}</Label>
                    <Input
                      data-testid="company-region-input"
                      placeholder="Lombardia"
                      className="h-10 rounded-lg border-stone-200 bg-white text-sm"
                      value={formData.company_region}
                      onChange={(e) => setFormData({ ...formData, company_region: e.target.value })}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="email" className="text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="nome@email.com"
                    className="pl-10 h-11 rounded-xl border-stone-200 bg-stone-50 focus:bg-white text-sm"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-11 rounded-xl border-stone-200 bg-stone-50 focus:bg-white text-sm"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Privacy checkbox */}
              <div className="flex items-start gap-3 mt-4">
                <input type="checkbox" id="privacy" data-testid="privacy-checkbox"
                  checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500" />
                <label htmlFor="privacy" className="text-sm text-stone-600">
                  {language === 'it' ? (
                    <>Accetto la <Link to="/privacy" target="_blank" className="text-emerald-700 hover:underline font-medium">Privacy Policy</Link> e autorizzo il trattamento dei miei dati personali ai sensi del GDPR.</>
                  ) : (
                    <>I accept the <Link to="/privacy" target="_blank" className="text-emerald-700 hover:underline font-medium">Privacy Policy</Link> and authorize the processing of my personal data under GDPR.</>
                  )}
                </label>
              </div>

              <Button
                type="submit"
                data-testid="register-submit-btn"
                disabled={loading || !privacyAccepted}
                className="w-full h-11 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold mt-4"
              >
                {loading ? (language === 'it' ? 'Caricamento...' : 'Loading...') : (language === 'it' ? 'Registrati' : 'Register')}
              </Button>
            </form>

            <p className="mt-4 text-center text-stone-600 text-sm">
              {language === 'it' ? 'Hai già un account?' : 'Already have an account?'}{' '}
              <Link to="/login" className="text-emerald-800 hover:underline font-medium">
                {language === 'it' ? 'Accedi' : 'Login'}
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-stone-500 hover:text-stone-700 text-sm">
              ← {language === 'it' ? 'Torna alla home' : 'Back to home'}
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
